// MACHINERY V2 (Stroje) — the product-image queue's data half (M2.7).
//
// Spec: data package §10 row `productImage (gallery)`: maxImages 10, per-file
// 11 MB, MIME jpeg/jpg/png/webp, EXACTLY ONE isPrimary, and the submit shape
// `{id (undefined when new), name (default image-{n}), content = src,
// displayOrder, isPrimary, size}`. Reference impl for every rule below:
// shared/components/gallery/MultiImageUpload.tsx (:26-32 accepted types, :81-95 the
// remaining-slots clamp, :107-114 the size check, :125 first-is-primary,
// :193-204 remove re-promotes, :208-217 toggle) plus MachineDialog.tsx:203-213
// (getFilesFromGallery).
//
// WHY NOT REUSE MultiImageUpload ITSELF: its first import is `@mui/material`.
// §3 keeps MUI out of the v2 import graph entirely, so v2 re-expresses the
// RULES (which are the data contract) over the v2 FormDropzone. Same call, same
// reasoning, as the Wave-1 V2_DOC_TYPE_KEYS duplicate and M2.5's
// DOC_MAX_FILE_SIZE_MB.
//
// THE CAP IS RESTATED, NOT IMPORTED, FOR A SECOND REASON: `MAX_FILE_SIZE_MB`
// lives in shared/models/models.ts (:422, `10 + 1`), whose first line imports
// AlertColor from @mui/material — a value import from that module would drag
// MUI in. M2.5's DOC_MAX_FILE_SIZE_MB restated the same number for the same
// reason; the two surfaces happen to agree at 11 and are still independent.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { MachineFileItem } from "../../models/models";

export const MACHINE_IMAGE_MAX_FILE_SIZE_MB = 11;
export const MACHINE_IMAGE_MAX_COUNT = 10;

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_EXTENSIONS = /\.(jpg|jpeg|png|webp)$/i;

/** A queued image. Create-only surface, so every entry is new and its `id` is
 *  undefined on submit — the VM's non-nullable Guid becomes Guid.Empty server
 *  side, which is the documented §10 behaviour. */
export interface MachineImage {
  key: string;
  src: string;
  name: string;
  size?: number;
  isPrimary: boolean;
}

export const isValidImageType = (file: File): boolean =>
  ACCEPTED_TYPES.includes(file.type) || (!!file.name && ACCEPTED_EXTENSIONS.test(file.name));

export const readAsDataUrl = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

/** Remove, re-promoting the first survivor when the primary went
 *  (MultiImageUpload.tsx:197-200) — "exactly one isPrimary" is an invariant. */
export const removeImage = (images: MachineImage[], key: string): MachineImage[] => {
  const removedWasPrimary = images.find((i) => i.key === key)?.isPrimary;
  const kept = images.filter((i) => i.key !== key);
  if (removedWasPrimary && kept.length > 0) kept[0] = { ...kept[0], isPrimary: true };
  return kept;
};

/** Toggle is really "set": exactly one row carries the flag. */
export const setPrimaryImage = (images: MachineImage[], key: string): MachineImage[] =>
  images.map((i) => ({ ...i, isPrimary: i.key === key }));

/** v1's getFilesFromGallery (MachineDialog.tsx:203-213), including its
 *  `undefined`-when-empty return and the `image-{n}` name fallback. */
export const filesFromGallery = (images: MachineImage[]): MachineFileItem[] | undefined => {
  if (images.length === 0) return undefined;
  return images.map((img, idx) => ({
    id: undefined,
    name: img.name || `image-${idx + 1}`,
    content: img.src,
    displayOrder: idx,
    isPrimary: img.isPrimary === true,
    size: img.size,
  }));
};

export interface AcceptResult {
  added: MachineImage[];
  /** i18n keys + params for the rows the queue refused, in v1's own order. */
  rejections: { key: string; params: Record<string, unknown> }[];
}

/**
 * The accept pipeline, in MultiImageUpload's order: clamp to the free slots
 * FIRST (and complain once if anything was cut), then per file check type, then
 * size, then read. A refused file never removes an accepted one — the M2.5
 * artboard-1004 rule, which this surface shares.
 */
export const acceptImages = async (
  current: MachineImage[],
  incoming: File[]
): Promise<AcceptResult> => {
  const rejections: AcceptResult["rejections"] = [];
  const remaining = MACHINE_IMAGE_MAX_COUNT - current.length;
  if (remaining <= 0) {
    return {
      added: [],
      rejections: [
        { key: "common:gallery.upload.tooManyFiles", params: { max: MACHINE_IMAGE_MAX_COUNT } },
      ],
    };
  }

  const batch = incoming.slice(0, remaining);
  if (incoming.length > remaining) {
    rejections.push({
      key: "common:gallery.upload.tooManyFiles",
      params: { max: MACHINE_IMAGE_MAX_COUNT },
    });
  }

  const added: MachineImage[] = [];
  for (const file of batch) {
    if (!isValidImageType(file)) {
      rejections.push({ key: "common:gallery.upload.invalidFormat", params: { name: file.name } });
      continue;
    }
    if (file.size > MACHINE_IMAGE_MAX_FILE_SIZE_MB * 1024 * 1024) {
      rejections.push({
        key: "common:gallery.upload.fileTooLarge",
        params: { name: file.name, size: MACHINE_IMAGE_MAX_FILE_SIZE_MB },
      });
      continue;
    }
    // Sequential on purpose: a bulk drop read in parallel holds every data-URI
    // in memory at once (the same call M2.5's docs queue made).
    // eslint-disable-next-line no-await-in-loop
    const content = await readAsDataUrl(file);
    if (!content) {
      rejections.push({ key: "common:gallery.upload.invalidFormat", params: { name: file.name } });
      continue;
    }
    added.push({
      key: `${file.name}:${file.size}:${file.lastModified}:${added.length}:${Date.now()}`,
      src: content,
      name: file.name,
      size: file.size,
      isPrimary: current.length === 0 && added.length === 0,
    });
  }

  return { added, rejections };
};
/**
 * Append an accepted batch to whatever the queue holds NOW.
 *
 * `acceptImages` awaits a FileReader per file, so `current` here is not
 * necessarily the array it clamped against — the user may have REMOVED a row
 * while the read ran. Re-reading the live queue at merge time is what stops a
 * stale write from resurrecting that removal. The one thing that re-read can
 * break is the "exactly one isPrimary" invariant: an accept flags its first
 * entry primary only when the queue was EMPTY when it started, so if the last
 * surviving row was removed mid-read the merged list would carry none.
 * Re-promote the first row, exactly as `removeImage` does above. (Two primaries
 * cannot occur: an accept only sets the flag against an empty queue, and the
 * caller serialises accepts.)
 */
export const mergeImages = (current: MachineImage[], added: MachineImage[]): MachineImage[] => {
  const next = [...current, ...added];
  if (next.length > 0 && !next.some((i) => i.isPrimary)) next[0] = { ...next[0], isPrimary: true };
  return next;
};
